import * as styles from "./DigestPanel.css";
import { reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { MessagesStore } from '../stores';
import { IAPanel } from '../../panels';
import { IAButton } from '../../button/Button';
import { ConditionalOutputFactory } from "../../../services/ConditionalOutputFactory";
import { IAMediaSelector } from "../../mediaSelector/MediaSelector";
import { Environment } from "../../../services/Environment";
import MessageService from "../../../services/MessageService";
import { FilterService } from "../Filter.service";
import { IMessage } from "../../../interfaces/IMessage";
import { IASpinner } from "../../spinner/Spinner";
import { IACheckbox } from "../../checkbox/Checkbox";
import { Helper } from "../../../Helper";
import { IDigestData } from "../../../interfaces/IDigestData";
import { IDigestMessage } from "../../../interfaces/IDigestMessage";
import { LocalizationService } from "../../../services/LocalizationService";
import { IAImageCropper } from "../../imageCropper/ImageCropper";
import ImageService from "../../../services/ImageService";
import { ICropSetting } from "../../../interfaces/ICropSetting";

export interface IProps {
  messagesStore?: MessagesStore;
  dataAutomationIdPrefix?: string;
  onClose?: () => void;
}

export interface IState {
  pageView: "edit" | "preview";
  header: string;
  headerEdited: boolean;
  description: string;
  footer: string;
  image: string;
  messagesSelected: MessagesSelected;
  isLoadingMessages: boolean;
  nextPagingInfo: string;
  messagesLoaded: IMessage[];
  isPreviewLoading: boolean,
  previewHtml: string;
  cropSetting: ICropSetting;
  isCropping: boolean;
}

@inject('messagesStore')
@observer
export class DigestPanel extends React.Component<IProps, IState>{
  private readonly localizationService = new LocalizationService();
  
  constructor(props: IProps) {
    super(props);
    this.state =  this.resetState();
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  componentDidMount(): void {
    reaction(() => this.props.messagesStore.digestPaneIsOpen, digestPaneIsOpen => {
      if (digestPaneIsOpen) {
        this.setState(this.resetState(), () => this.loadPage());
      }
    });
  }

  render() {
    return (
      <IAPanel
        showNavigationBar={false}
        open={this.props.messagesStore.digestPaneIsOpen}
        transition='slide'
        width={700}
        close={() => this.props.messagesStore.digestPaneIsOpen = false}
      >
        <div style={{display: this.state.pageView === "edit" ? "block" : "none"}}>
          <section className={styles.menu}>
            <IAButton label={this.localizationService.strings.Messages_DigestPanel_Preview}
              disbaled={ConditionalOutputFactory
                .WithDefault(true)
                .OnCondition(!!this.state.header && !!this.state.image && this.state.messagesSelected.isAnySelected() && !this.state.isCropping, false).Last()}
              style={{backgroundColor: "rgb(28, 137, 157)", height:"30px", padding: "0px 20px", color: "#fff", marginLeft: "auto", borderRadius: "4px"}}
              onClick={() => this.generatePreview()}
            />
            <IAButton icon="Close"
              height={30}
              iconColor={"#000"}
              fontSize={16}
              iconSize={16}
              style={{padding: "0 4px"}}
              onClick={() => this.props.messagesStore.digestPaneIsOpen = false}/>
          </section>

          <div className={styles.scrollable}>
            <h2 className={styles.header}>Title and description</h2>

            <section className={[styles.spacing, styles.positionRelative].join(' ')}>
              <label className={styles.mediaSelectorLabel}>{this.localizationService.strings.Messages_DigestPanel_SelectAnImage} <span className={styles.important}>*</span></label>
              {this.state.image && !this.state.isCropping ? 
              <IAButton
                buttonColor="rgba(0,0,0,.3)"
                mouseOverButtonColor="rgba(0,0,0,.2)"
                icon="Crop"
                iconMarginTop={3}
                borderRadius={5}
                style={{
                  position: "absolute",
                  top: 49,
                  left: 33,
                  zIndex: 1,
                  margin: 0,
                }}
                onClick={() => this.setState({ isCropping: true })}
              /> :<></> }
              {this.state.isCropping ?
              <IAImageCropper
                token={this.props.messagesStore.token}
                tenant={this.props.messagesStore.tenant}
                environment={this.props.messagesStore.environment}
                image={ImageService.toDirectUrl(this.state.image)}
                showButtons={true}
                enableZoom={true}
                initialCrop={this.state.cropSetting}
                height={400}
                aspect={3.6}
                onClick={() => {}}
                onUpdate={() => {}}
                onSave={(crop) => {
                  const newImage = Helper.getImageUrl(
                    Environment.getEnvironmentForImageAPI(this.props.messagesStore.environment),
                    this.props.messagesStore.tenant,
                    this.props.messagesStore.token,
                    ImageService.toDirectUrl(this.state.image),
                    null,
                    "Full",
                    crop);
                  this.setState({ cropSetting: crop, image: newImage, isCropping: false });
                }}
                onCancel={() => this.setState({ isCropping: false })}
                /> : 
              <IAMediaSelector
                label={''}
                isMobile={false}
                fileType="image"
                showPreview={true}
                highlightColor={this.props.messagesStore.highlightColor}
                token={this.props.messagesStore.token}
                tenant={this.props.messagesStore.tenant}
                selectedImage={this.state.image}
                environment={this.props.messagesStore.environment}
                uploadUrl={`${Environment.getEnvironmentForMainAPI(this.props.messagesStore.environment)}/api/${this.props.messagesStore.tenant}.sharepoint.com/image?site=IntraActive-Messages&relativeUrl=${encodeURIComponent("Lists/PublicRead_MessageAssets")}`}
                style={{
                  margin: 10,
                }}
                onChange={(image: string) => this.setState({ image: image, cropSetting: null, isCropping: !!image }) }
              /> }
            </section>

            <section className={styles.spacing + " " + styles.form}>
              <label>
                {this.localizationService.strings.Messages_DigestPanel_EmailTitleAndHeader} <span className={styles.important}>*</span>
                <div className={ConditionalOutputFactory
                    .WithDefault(styles.popup, styles.arrowBottom)
                    .OnCondition(this.state.headerEdited && !this.state.header, styles.showPopup).AsClassName()}>
                  <div className={styles.popupWrapper}>
                    <p>{this.localizationService.strings.Messages_DigestPanel_AnImageIsRequired}</p>
                  </div>
                </div>
                <input type="text"
                  className={ConditionalOutputFactory
                    .WithDefault("")
                    .OnCondition(this.state.headerEdited && !this.state.header, styles.invalid).Last()}
                  value={this.state.header}
                  onChange={(e) => {
                    this.setState({ header: e.target.value, headerEdited: true });
                  }}/>
              </label>
              <label>
                {this.localizationService.strings.Messages_DigestPanel_Description}
                <textarea
                  value={this.state.description}
                  onChange={(e) => {
                    this.setState({ description: e.target.value });
                  }} />
              </label>
              <label>
                {this.localizationService.strings.Messages_DigestPanel_Footer}
                <input type="text" 
                  value={this.state.footer}
                  onChange={(e) => {
                    this.setState({ footer: e.target.value });
                  }}/>
              </label>
            </section>
            <h2 className={styles.header}>{this.localizationService.strings.Messages_DigestPanel_ChooseAndSortMessages}</h2>
            <section className={styles.spacing}>
            <div>
                {this.state.messagesLoaded.map((m) => 
                <div key={m.id} className={styles.checkbox}
                  draggable
                  onDrag={(event) => {
                    event.preventDefault();
                  }}
                  onDragOver={e => {
                    if (e.preventDefault) {
                      e.preventDefault();
                    }
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDragStart={e => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', m.id.toString());
                  }}
                  onDrop={e => {
                    e.stopPropagation();
                    const id = parseInt(e.dataTransfer.getData("text/plain"));
                    const indexOfNewItem = this.state.messagesLoaded.findIndex(x => x.id === id);
                    const indexOfCurrentItem = this.state.messagesLoaded.findIndex(x => x.id === m.id);
                    const removedItems = this.state.messagesLoaded.splice(indexOfNewItem, 1);
                    this.state.messagesLoaded.splice(indexOfCurrentItem, 0, ...removedItems);
                    this.setState({messagesLoaded: this.state.messagesLoaded});
                  }}>
                  <IACheckbox label={m.title} name={m.id.toString()} onChange={(checked, name) => {
                        this.state.messagesSelected[name] = checked;
                        this.setState({ messagesSelected: this.state.messagesSelected });
                      }}
                      highlightColor={this.props.messagesStore.highlightColor}
                      borderColor={this.props.messagesStore.highlightColor}
                      isChecked={!!this.state.messagesSelected[m.id.toString()]}
                      styles={{display:"block"}}
                      labelStyles={{textOverflow: "ellipsis", overflow: "hidden", cursor: "move"}}
                    />
                </div>)
                }
                <div className={styles.moreItems}>
                  {this.state.nextPagingInfo && !this.state.isLoadingMessages ? <IAButton label={this.localizationService.strings.Messages_DigestPanel_LoadMore} onClick={() => this.loadPage()} style={{float:"none"}} /> : null}
                  {this.state.isLoadingMessages ? <IASpinner /> : null}
                </div>
              </div>
            </section>
          </div>
        </div>
        <div style={{display: this.state.pageView === "preview" ? "block" : "none"}} className={styles.previewPage}>
          <section className={styles.menu}>
            <IAButton label={this.localizationService.strings.Messages_DigestPanel_Edit}
              disbaled={ConditionalOutputFactory
                .WithDefault(true)
                .OnCondition(!!this.state.header && !!this.state.image && this.state.messagesSelected.isAnySelected(), false).Last()}
              style={{backgroundColor: "transparent", color: "rgb(28, 137, 157)", height:"30px", padding: "0px 20px", marginLeft: "auto", borderRadius: "4px"}}
              onClick={() => this.setState({pageView: "edit"})}
            />
            <IAButton label={this.localizationService.strings.Messages_DigestPanel_Generate}
              disbaled={ConditionalOutputFactory
                .WithDefault(true)
                .OnCondition(!!this.state.header && !!this.state.image && this.state.messagesSelected.isAnySelected(), false).Last()}
              style={{backgroundColor: "rgb(28, 137, 157)", height:"30px", padding: "0px 20px", color: "#fff", borderRadius: "4px"}}
              onClick={() => this.generateFile().then(() => this.setState({pageView: "edit"}))}
            />
            <IAButton icon="Close"
              height={30}
              iconColor={"#000"}
              fontSize={16}
              iconSize={16}
              style={{padding: "0 4px"}}
              onClick={() => this.props.messagesStore.digestPaneIsOpen = false}/>
          </section>

          <div className={styles.scrollable}>
            {!this.state.isPreviewLoading ? <section className={[styles.spacing, styles.design].join(' ')} dangerouslySetInnerHTML={{__html: this.state.previewHtml}}></section> : null}
            {this.state.isPreviewLoading ? <IASpinner /> : null}
          </div>
        </div>
      </IAPanel>
    );
  }

  private async loadPage(): Promise<void> {
    this.setState({
      isLoadingMessages: true,
    });
    const env = this.props.messagesStore.environment ?? "Production";
    await this.props.messagesStore.getInstance();
    const messageResult = await MessageService.getMessages(
      env,
      this.props.messagesStore.token,
      this.props.messagesStore.tenant,
      this.props.messagesStore.instance.instanceName,
      false,
      FilterService.get(false,
        this.props.messagesStore.filterData,
        false,
        [],
        10,
        this.state.nextPagingInfo,
        this.props.messagesStore.component),
      true,
      null,
      false);
    this.state.messagesLoaded.push(...messageResult.messages);
    this.setState({
      nextPagingInfo: messageResult.pagingInfo,
      messagesLoaded: this.state.messagesLoaded,
      isLoadingMessages: false,
    });
  }

  private async generatePreview(): Promise<void> {
    const digestData: IDigestData = {
      coverImageLink: this.state.image,
      descriptionText: this.state.description,
      footerText: this.state.footer,
      titleText: this.state.header,
      inputMessages: Array.from(this.state.messagesSelected.asIDigestMessage(this.state.messagesLoaded, this.props.messagesStore)),
    }
    this.setState({ isPreviewLoading: true, pageView: "preview" });
    const previewResponse = await MessageService.getDigestPreview(this.props.messagesStore.environment, this.props.messagesStore.tenant, digestData);
    this.setState({ isPreviewLoading: false, previewHtml: previewResponse.htmlPreview });
  }

  private async generateFile(): Promise<void> {
    const digestData: IDigestData = {
      coverImageLink: this.state.image,
      descriptionText: this.state.description,
      footerText: this.state.footer,
      titleText: this.state.header,
      inputMessages: Array.from(this.state.messagesSelected.asIDigestMessage(this.state.messagesLoaded, this.props.messagesStore)),
    }
    MessageService.createDigestFile(this.props.messagesStore.environment, this.props.messagesStore.tenant, digestData);
  }

  private resetState() : IState {
    return {
      pageView: "edit",
      headerEdited: false,
      header: '',
      description: '',
      footer: '',
      image: '',
      messagesSelected: new MessagesSelected(),
      isLoadingMessages: true,
      nextPagingInfo: "",
      messagesLoaded: [],
      isPreviewLoading: false,
      previewHtml: "",
      cropSetting: null,
      isCropping: false,
    };
  }
}

class MessagesSelected {
  [id: number]: boolean;

  public isAnySelected(): boolean {
    for(const n in this) {
      if (typeof(this[n]) === "boolean" && !!this[n]) {
        return true;
      }
    }
  }

  public *asIDigestMessage(messages: IMessage[], messagesStore: MessagesStore): Generator<IDigestMessage> {
    for(const message of messages) {
      if (typeof(this[message.id]) === "boolean" && !!this[message.id]) {
        yield {
          id: message.id,
          imageLink: Helper.getImageUrl(Environment.getEnvironmentForImageAPI(messagesStore.environment), messagesStore.tenant, messagesStore.token, message.image, null, "StandardLarge", message.iaConfiguration.cropSettings),
          messageLink: `${Environment.getEnvironmentForLinkPortal(messagesStore.environment)}/${messagesStore.tenant}/${messagesStore.instance.instanceName}/${message.id}`,
          teaser: message.teaser ?? message.content,
          title: message.title,
          userName: message.editor.displayName,
          dateTimeOffset: message.modified
        };
      }
    }
  }
}